import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { MagneticButton } from '../components/animated/MagneticButton';
import { AnimatedSection } from '../components/animated/AnimatedSection';
import { StatCounter } from '../components/animated/StatCounter';
import { FeatureCard } from '../components/animated/FeatureCard';
import { CurvedPhotoGallery } from '../components/animated/CurvedPhotoGallery';
import { LogoSlider } from '../components/animated/LogoSlider';
import { ChatBlurb } from '../components/animated/ChatBlurb';
import { useScrollReveal, useStaggerReveal } from '../hooks/useGSAP';
import { Users, Target, TrendingUp, Lightbulb, Sparkles, Zap, Award, BookOpen, Quote, Star } from 'lucide-react';
export function HomePage() {
  const heroRef = useScrollReveal();
  const statsRef = useStaggerReveal(150);
  const navigate = useNavigate();
  const impactPhotos = [{
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/fieYNmErKpAxE9njqoZfbs/IMG_20251023_125324.jpg',
    alt: 'Leadership Training Session'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/tNcsU3kySKAjPSzKUSfQ7D/IMG_20251023_125626.jpg',
    alt: 'Professional Development'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/ghhzDMnxYyJEs2QNeZXGWC/1755202594528.jpg',
    alt: 'Youth Empowerment'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/muHHmEBKQ1boiHjLaMty3n/IMG_6586.jpg',
    alt: 'Skills Development'
  }];
  const trustedLogos = [{
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/q33zkxUoeevpbURgJWNH4Z/Edo-State-2048x2048.png',
    alt: 'Edo State Government'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/3c1y2EtqxE3cZ22j3LfvAt/Firstbank-scaled.png',
    alt: 'First Bank'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/pGmJB3M7dmG185ouLH3tdL/Guinness-scaled.png',
    alt: 'Guinness'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/vEe7mEmaq8bYaMkACwoyZ2/Patiti-scaled.png',
    alt: 'Patiti'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/nit6GmCfjTk1NhaNrGV48S/Unilever-1-scaled.png',
    alt: 'Unilever'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/6726Wr7TFseuzgbfXWVCuc/etinpower_logo.webp',
    alt: 'Etinpower'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gkxuUBnqjFnuHSrzppQc3m/Igbinedion_education_logo.jpg',
    alt: 'Igbinedion Education Centre'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/fdjcuHfL5jcJervWyfDggM/Seplat_Logo_2_%281%29.jpg',
    alt: 'Seplat Energy'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/nmSr7rTJkqWzi93Moxk1To/TAFTA-LOGO-1.jpg',
    alt: 'TAFTA'
  }];
  return <div className="flex flex-col min-h-screen bg-white" data-id="element-997">
      <main className="flex-grow pt-20" data-id="element-998">
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-id="element-999">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-green-50" data-id="element-1000" />
          <div className="absolute top-20 right-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob" data-id="element-1001" />
          <div className="absolute bottom-20 left-10 w-72 h-72 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000" data-id="element-1002" />

          <ChatBlurb name="Abdullahi Ismaila Osianor" message="SkillzHQ transformed how I think, work, and communicate professionally! 🚀" avatarUrl="https://uploadthingy.s3.us-west-1.amazonaws.com/nCVDGCQW6wQuTBjeaBY2wJ/Osianor_Abdullahi.jpg" position="left" data-id="element-1003" />
          <ChatBlurb name="Eunice Olabode" message="This course was an eye-opener. Best training investment! 💯" avatarUrl="https://uploadthingy.s3.us-west-1.amazonaws.com/wvPJkKyKJVWpzfT4CG3UPv/Eunice_Olabode.jpg" position="right" data-id="element-1004" />
          <ChatBlurb name="Ayobami Phebian Mehunro" message="Nothing short of exceptional. Culture change that works! ✨" avatarUrl="https://uploadthingy.s3.us-west-1.amazonaws.com/odGUibT465TjpGfyLGrZ6p/Ayobami_Phebian%2C_Mehunro.jpg" position="center" data-id="element-1005" />

          <div className="container mx-auto px-6 lg:px-8 relative z-10" data-id="element-1006">
            <div className="max-w-5xl mx-auto text-center" ref={heroRef as React.RefObject<HTMLDivElement>} data-id="element-1007">
              <div className="inline-block mb-6" data-id="element-1008">
                <span className="inline-flex items-center px-4 py-2 rounded-full bg-blue-50 text-blue-700 text-sm font-medium border border-blue-100" data-id="element-1009">
                  <Sparkles className="w-4 h-4 mr-2" data-id="element-1010" />
                  Transforming Organizations Across Africa
                </span>
              </div>

              <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight text-gray-900 mb-8 leading-tight" data-id="element-1011">
                Driving Innovation through People
                <br data-id="element-1012" />
                <span className="bg-gradient-to-r from-blue-600 via-blue-700 to-green-600 bg-clip-text text-transparent" data-id="element-1013">
                  Transformation
                </span>
              </h1>

              <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed" data-id="element-1014">
                SkillzHQ empowers people and strengthens institutions through
                mindset and culture transformation, leadership development,
                strategy execution, and high-impact, in-demand skills
                development.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center" data-id="element-1015">
                <MagneticButton variant="primary" size="lg" onClick={() => navigate('/academy')} data-id="element-1016">
                  Get Started Today
                </MagneticButton>
                <Button variant="outline" size="lg" onClick={() => navigate('/academy')} data-id="element-1017">
                  Explore Our Programs
                </Button>
              </div>
            </div>
          </div>
        </section>

        <CurvedPhotoGallery photos={impactPhotos} title="Real Impact, Real Transformation" subtitle="Empowering thousands of professionals across Africa through hands-on training and community-driven development" data-id="element-1018" />

        <LogoSlider logos={trustedLogos} data-id="element-1019" />

        <AnimatedSection className="py-24 bg-white" data-id="element-1020">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-1021">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 max-w-4xl mx-auto" ref={statsRef as React.RefObject<HTMLDivElement>} data-id="element-1022">
              <StatCounter end={2500} suffix="+" label="Trained" data-id="element-1023" />
              <StatCounter end={5000} suffix="+" label="Reached" data-id="element-1024" />
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection className="py-24 bg-gradient-to-b from-white to-gray-50" data-id="element-1025">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-1026">
            <div className="text-center mb-20" data-id="element-1027">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-1028">
                Our Focus Areas
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1029">
                Comprehensive solutions for organizational excellence
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto" data-id="element-1030">
              <FeatureCard icon={<Users className="w-7 h-7 text-blue-600" data-id="element-1032" />} title="Leadership Development" description="Empowering leaders at all levels to drive growth, foster innovation, and build resilient teams." data-id="element-1031" />
              <FeatureCard icon={<Target className="w-7 h-7 text-blue-600" data-id="element-1034" />} title="Culture Transformation" description="Creating high-performance cultures built on collaboration, accountability, and shared purpose." data-id="element-1033" />
              <FeatureCard icon={<TrendingUp className="w-7 h-7 text-blue-600" data-id="element-1036" />} title="Strategy Execution" description="Translating visions into actionable plans that deliver measurable, sustainable results." data-id="element-1035" />
              <FeatureCard icon={<Lightbulb className="w-7 h-7 text-green-600" data-id="element-1038" />} title="Youth Engagement" description="Equipping young Africans with essential skills for entrepreneurship and employment." data-id="element-1037" />
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection className="py-24 bg-white" data-id="element-1039">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-1040">
            <div className="text-center mb-20" data-id="element-1041">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-1042">
                Why Choose SkillzHQ?
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto" data-id="element-1043">
              <div className="text-center group" data-id="element-1044">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300" data-id="element-1045">
                  <Zap className="w-10 h-10 text-blue-600" data-id="element-1046" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3" data-id="element-1047">
                  Expert-Driven
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1048">
                  Led by industry professionals with proven track records
                </p>
              </div>

              <div className="text-center group" data-id="element-1049">
                <div className="w-20 h-20 bg-gradient-to-br from-green-50 to-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300" data-id="element-1050">
                  <Target className="w-10 h-10 text-green-600" data-id="element-1051" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3" data-id="element-1052">
                  Customized
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1053">
                  Tailored solutions for your unique challenges and goals
                </p>
              </div>

              <div className="text-center group" data-id="element-1054">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300" data-id="element-1055">
                  <Award className="w-10 h-10 text-blue-600" data-id="element-1056" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3" data-id="element-1057">
                  Measurable Impact
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1058">
                  Tangible, lasting improvements you can track
                </p>
              </div>

              <div className="text-center group" data-id="element-1059">
                <div className="w-20 h-20 bg-gradient-to-br from-green-50 to-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300" data-id="element-1060">
                  <BookOpen className="w-10 h-10 text-green-600" data-id="element-1061" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3" data-id="element-1062">
                  Flexible Delivery
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1063">
                  In-person, virtual, or hybrid—we adapt to you
                </p>
              </div>
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection className="py-24 bg-gradient-to-b from-gray-50 to-white" data-id="element-1064">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-1065">
            <div className="text-center mb-16" data-id="element-1066">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-1067">
                Who We Serve
              </h2>
            </div>

            <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto" data-id="element-1068">
              {['Multinationals', 'Startups', 'Young Professionals', 'Venture Capitalists', 'Public Sector'].map(item => <div key={item} className="px-8 py-4 rounded-full bg-white border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300 hover:-translate-y-1" data-id="element-1069">
                  <span className="text-gray-700 font-medium" data-id="element-1070">{item}</span>
                </div>)}
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection className="py-24 bg-white" data-id="element-1071">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-1072">
            <div className="text-center mb-16" data-id="element-1073">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-1074">
                What Our Learners Say
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1075">
                Real stories from EA Course graduates
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-1076">
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 border border-blue-100 shadow-lg hover:shadow-xl transition-shadow duration-300" data-id="element-1077">
                <div className="flex gap-1 mb-4" data-id="element-1078">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-1079" />)}
                </div>
                <Quote className="w-8 h-8 text-blue-600 mb-4" data-id="element-1080" />
                <p className="text-gray-700 mb-6 leading-relaxed italic" data-id="element-1081">
                  "The Executive Assistant Mastery Course transformed how I
                  think, work, and communicate professionally. It exceeded every
                  expectation."
                </p>
                <div data-id="element-1082">
                  <p className="font-bold text-gray-900" data-id="element-1083">
                    Abdullahi Ismaila Osianor
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-1084">
                    Aspiring Executive Assistant
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-white rounded-2xl p-8 border border-green-100 shadow-lg hover:shadow-xl transition-shadow duration-300" data-id="element-1085">
                <div className="flex gap-1 mb-4" data-id="element-1086">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-1087" />)}
                </div>
                <Quote className="w-8 h-8 text-green-600 mb-4" data-id="element-1088" />
                <p className="text-gray-700 mb-6 leading-relaxed italic" data-id="element-1089">
                  "This course was an eye-opener. The modules were practical,
                  detailed, and relevant. I now feel more confident in my EA
                  journey."
                </p>
                <div data-id="element-1090">
                  <p className="font-bold text-gray-900" data-id="element-1091">Eunice Olabode</p>
                  <p className="text-sm text-gray-600" data-id="element-1092">
                    Practicing Executive Assistant
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 border border-blue-100 shadow-lg hover:shadow-xl transition-shadow duration-300" data-id="element-1093">
                <div className="flex gap-1 mb-4" data-id="element-1094">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-1095" />)}
                </div>
                <Quote className="w-8 h-8 text-blue-600 mb-4" data-id="element-1096" />
                <p className="text-gray-700 mb-6 leading-relaxed italic" data-id="element-1097">
                  "Nothing short of exceptional. The course broadened my
                  understanding and gave me clarity on how to excel as a
                  world-class EA."
                </p>
                <div data-id="element-1098">
                  <p className="font-bold text-gray-900" data-id="element-1099">
                    Ayobami Phebian Mehunro
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-1100">
                    Aspiring Executive Assistant
                  </p>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection className="py-32 bg-gradient-to-br from-blue-600 via-blue-700 to-green-600 relative overflow-hidden" data-id="element-1101">
          <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:32px_32px]" data-id="element-1102" />
          <div className="container mx-auto px-6 lg:px-8 relative z-10" data-id="element-1103">
            <div className="max-w-4xl mx-auto text-center" data-id="element-1104">
              <h2 className="text-4xl md:text-6xl font-bold text-white mb-8" data-id="element-1105">
                Ready to Transform Your Organization?
              </h2>
              <p className="text-xl text-blue-100 mb-12 max-w-2xl mx-auto" data-id="element-1106">
                Join thousands of professionals who have accelerated their
                growth with SkillzHQ
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1107">
                <MagneticButton variant="secondary" size="lg" onClick={() => navigate('/academy')} data-id="element-1108">
                  Get Started Today
                </MagneticButton>
                <MagneticButton variant="outline" size="lg" onClick={() => navigate('/contact')} data-id="element-1109">
                  Contact Us
                </MagneticButton>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </main>
    </div>;
}